import React, { useState, useRef, useEffect } from 'react';

import { Footer, Header } from '$partials/global';
import ModuleDispatcher from '$partials/modules/dispatcher';
import { SimpleImage } from '$partials/media';
import { Button } from '$partials/ui';
import { Select } from '$partials/fields';
import IconArrow from '$partials/icons/icon-arrow';
import IconChevron from '$partials/icons/icon-chevron';
import IconLogoHomepage from '$partials/icons/icon-logo-homepage';
import { CtaBannerBlock } from '$partials/modules/blocks';
import { HomeSchema } from '$partials/home';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/pages.d.ts').HomePage}
 */
const HomePage = (props) => {
    const page = props.data.page;
    const [randomCoverMedia, setRandomCoverMedia] = useState(null);
    const [selectedMarket, setSelectedMarket] = useState(null);
    const [activeTab, setActiveTab] = useState('buy');
    const [showLogo, setShowLogo] = useState(false);

    const heroMediaRef = useRef();

    useEffect(() => {
        if (page?.coverMediaItems && page.coverMediaItems?.length > 0) {
            const randomIndex = Math.floor(Math.random() * page.coverMediaItems.length);
            setRandomCoverMedia(page.coverMediaItems[randomIndex]);
        }
    }, []);

    useEffect(() => {
        if (heroMediaRef.current) {
            const target = heroMediaRef.current;
            const observer = new IntersectionObserver((entries) => {
                setShowLogo(!entries?.[0].isIntersecting);
            }, {
                rootMargin: '0px',
                scrollMargin: '-58px',
                threshold: 1.0,
            });
            observer.observe(target);

            return () => observer.unobserve(target);
        }
    }, []);

    return (
        <>
            <HomeSchema
                footer={props.data.footer}
                page={page}
                socialLinks={props.data.footer?.socialLinks}
                url={props?.location} />

            <Header
                {...props.data.header}
                showLogo={showLogo}
                variant='HOME' />

            <figure className='fixed w-full h-[90svh] sm:h-[70svh] overflow-clip z-0 bg-grey-dark-800'>
                <div className='relative w-full h-full'>
                    {randomCoverMedia && (
                        <>
                            {randomCoverMedia?.media?.image &&
                                <SimpleImage
                                    {...randomCoverMedia.media.image}
                                    className='w-full h-full object-cover' />
                            }
                            {randomCoverMedia?.media?.video &&
                                <video
                                    autoPlay={true}
                                    className={'w-full h-full object-cover'}
                                    loop={true}
                                    muted={true}
                                    playsInline={true}
                                    poster={randomCoverMedia.media.video.poster?.url}
                                    src={randomCoverMedia.media.video.src} />
                            }
                        </>
                    )}

                    {/* Fade */}
                    <div
                        className='absolute inset-0 pointer-events-none mix-blend-darken'
                        style={{
                            background: 'linear-gradient(180deg, rgba(44, 43, 42, 0.00) 85.11%, rgba(44, 43, 42, 0.80) 100%), linear-gradient(180deg, rgba(44, 43, 42, 0.00) 3.89%, rgba(44, 43, 42, 0.50) 52.98%, rgba(44, 43, 42, 0.10) 80.25%, rgba(44, 43, 42, 0.20) 107.03%)'
                        }} />

                    {/* Link to PDP */}
                    {randomCoverMedia?.page && (
                        <figcaption className='absolute bottom-45 py-7 pl-16 right-16 sm:right-108 text-right md:text-left text-grey-grey-100'>
                            <Button
                                as='a'
                                className='type-style-eyebrow'
                                href={randomCoverMedia?.page?.relativeUrl}>
                                <span>
                                    {randomCoverMedia?.page?.displayAddress}
                                </span>
                                <IconArrow className='ml-8 inline-block h-[7px]' />
                            </Button>
                        </figcaption>
                    )}

                    {/* Search Overlay */}
                    <div className='absolute inset-0 flex flex-col items-center justify-center gap-32 pointer-events-none'>
                        {/* Logo */}
                        <IconLogoHomepage className='text-grey-grey-100 h-[84px] w-[120px] -mt-40' />

                        {/* Search Form */}
                        <div className='bg-main-background-alt rounded-[2px] flex items-start pointer-events-auto w-[90vw] max-w-[538px] sm:w-[538px]'>
                            <div className='flex-1 flex items-center h-48 overflow-clip rounded-[2px]'>
                                <div className='bg-main-background-alt flex-1 flex h-full items-center overflow-clip pl-8'>
                                    {/* Buy/Rent Tabs */}
                                    <div className='flex gap-2 w-[123px] pr-8'>
                                        <Button
                                            as='button'
                                            className={`flex-1 px-12 py-8 rounded-[2px] type-style-eyebrow ${
                                                activeTab === 'buy'
                                                    ? 'bg-grey-grey-300 text-main-text-primary'
                                                    : 'bg-transparent hover:bg-button-secondary-transparent-background-hover text-main-text-secondary'
                                            }`}
                                            onClick={() => setActiveTab('buy')}>
                                            Buy
                                        </Button>
                                        <Button
                                            as='button'
                                            className={`flex-1 px-12 py-8 rounded-[2px] type-style-eyebrow ${
                                                activeTab === 'rent'
                                                    ? 'bg-grey-grey-300 text-main-text-primary'
                                                    : 'bg-transparent hover:bg-button-secondary-transparent-background-hover text-main-text-secondary'
                                            }`}
                                            onClick={() => setActiveTab('rent')}>
                                            Rent
                                        </Button>
                                    </div>

                                    {/* Divider */}
                                    <div className='h-full w-[1px] bg-grey-grey-300' />

                                    {/* Market Dropdown */}
                                    <div className='grow h-full flex items-center gap-14'>
                                        <Select
                                            contentAlign='start'
                                            contentClassName='min-w-[calc(var(--radix-popper-anchor-width))] z-50'
                                            contentPosition='popper'
                                            contentSide='bottom'
                                            contentSideOffset={5}
                                            name='market'
                                            onChange={(value) => {
                                                setSelectedMarket(value);
                                            }}
                                            options={[{ label: 'Choose location', value: null }].concat(props.data?.allMarkets?.map(market => ({
                                                label: market.title,
                                                value: JSON.stringify(market)
                                            })) || [])}
                                            placeholder='choose location'
                                            triggerClassName='w-full relative pl-12'
                                            value={selectedMarket}/>
                                    </div>
                                </div>
                            </div>

                            {/* Submit Button */}
                            <Button
                                as='a'
                                className='button-brand-filled flex items-center justify-center rounded-[2px] shrink-0 h-48 w-48'
                                href={(() => {
                                    if (!selectedMarket) {return null;}
                                    const market = JSON.parse(selectedMarket);
                                    return activeTab === 'buy'
                                        ? market.salePropertyListingPage?.relativeUrl
                                        : market.rentalPropertyListingPage?.relativeUrl;
                                })()}>
                                <IconChevron
                                    className='-rotate-90 text-button-brand-filled-text' />
                            </Button>
                        </div>
                    </div>
                </div>
            </figure>

            <div className='relative'>
                <div
                    className='mt-[90svh] sm:mt-[70svh]'
                    ref={heroMediaRef} />

                <main
                    id='main-content'>
                    <section className='module-cta-banner theme-light-alt bg-main-background relative z-10'>
                        <div className='layout-container layout-site-outer-padding py-24 sm:py-48 md:py-64'>
                            <div className='flex flex-col gap-y-12 col-wrapper'>
                                {page?.eyebrow &&
                                    <p className='col-padding type-style-eyebrow text-main-brand-text mb-10'>
                                        {page.eyebrow}
                                    </p>
                                }
                                {page?.displayTitle &&
                                    <h1 className='w-full sm:w-10/12 col-padding type-style-1 text-main-text-primary'>
                                        {page.displayTitle}
                                    </h1>
                                }
                            </div>
                        </div>
                    </section>
                    {page?.homePageModules?.length > 0 && (
                        <section className='theme-light-alt relative'>
                            {page.homePageModules.map((module, i) => (
                                <ModuleDispatcher
                                    {...module}
                                    key={i + module?.id} />
                            ))}
                        </section>
                    )}
                </main>
            </div>

            <CtaBannerBlock
                body={props.data.footer?.getInTouchBodyText}
                heading={props.data.footer?.getInTouchHeadingText}
                opensDialog={true} />

            <Footer {...props.data.footer} />
        </>
    );
};

/* ========================================================================= */
/* Exports */
/* ========================================================================= */

export default HomePage;
